import { memo, type Ref, useCallback, useImperativeHandle, useRef } from "react";
import { useDispatch } from "react-redux";
import { useWindowVirtualizer } from "@tanstack/react-virtual";

import { makeObserveWindowOffset, makeWindowScrollOffset } from "~/features/cart/cart/utils/TanstackVirtualUtils";
import AlternateProducts from "~/features/cart/cart/components/AlternateProducts";
import { CartEntry } from "~/features/cart/cartV2/CartEntry";
import { ConnectedEntriesChain } from "~/features/cart/cartV2/ConnectedEntriesChain";
import type { ProductTableRowViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";
import { buildEntryRowId } from "~/features/cart/cartV2/utils/buildTableId";
import { cartPageLoadThunk } from "~/features/cart/cart/redux/cartPageLoadThunk";
import { Box } from "~/common/mui";

export interface ProductEntryListRef {
  scrollToIndex: (index: number) => void;
  scrollToRowId: (rowId: string) => void;
  scrollToProductGroupNumber: (productGroupNumber: number) => void;
}

interface ListProps {
  rows: ProductTableRowViewModel[];
  editMode?: boolean;
  scrollRef?: Ref<ProductEntryListRef>;
  onCloseAlternateRow: (rowId: string) => void;
  onDeleteProduct: (cartEntryId: number) => void;
  onDeleteLine: (args: { cartEntryId: number; lineItemId: number }) => void;
  onCopyLine: (packEntryId: number) => void;
  getAlternateInsertPosition: (anchor: number | null) => number | undefined;
  "data-test-id"?: string;
}

export interface InsertedItem {
  index: number;
  code: string;
  designation: string;
}

const RegularProductEntryList = ({
  rows,
  editMode,
  scrollRef,
  onCloseAlternateRow,
  onDeleteProduct,
  onDeleteLine,
  onCopyLine,
  getAlternateInsertPosition,
  "data-test-id": testId,
}: ListProps) => {
  const dispatch = useDispatch();
  const virtualListContainerRef = useRef<HTMLDivElement>(null);
  const rowIndexById = new Map(rows.map((row, index) => [row.rowId, index]));

  const scrollToVirtualIndex = (index: number) => {
    if (index < 0 || index >= rows.length) {
      return;
    }

    rowVirtualizer.scrollToIndex(index);

    const rowId = rows[index]?.rowId;
    if (rowId) {
      virtualListContainerRef.current?.querySelector(`[data-row-id="${rowId}"]`)?.scrollIntoView({
        block: "center",
      });
    } else {
      virtualListContainerRef.current?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({
        block: "center",
      });
    }
  };

  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    getItemKey: (virtualIndex) => rows[virtualIndex]?.rowId ?? String(virtualIndex),
    estimateSize: (virtualIndex) => {
      const row = rows[virtualIndex];

      if (!row) {
        return 460;
      }

      if (row.kind === "alternate") {
        return 296; // height of AlternateProducts
      }

      if (row.kind === "chain") {
        return 40 - 24 - 2; // ConnectedEntriesChain SVG height minus padding of regular entires - px for border overlap
      }

      return Math.max(460, 188 + row.details.lines.length * 245);
    },
    overscan: 8,
    // @ts-expect-error TanStack virtualizer expects a narrower callback signature here.
    observeElementOffset: makeObserveWindowOffset(virtualListContainerRef),
    scrollToFn: makeWindowScrollOffset(virtualListContainerRef),
    scrollPaddingEnd: 200,
    scrollPaddingStart: 200,
    shouldAdjustScrollPositionOnItemSizeChange: () => true,
  });

  useImperativeHandle(scrollRef, () => ({
    scrollToIndex: (index) => {
      scrollToVirtualIndex(index);
    },
    scrollToRowId: (rowId) => {
      const index = rowIndexById.get(rowId);

      if (index !== undefined) {
        scrollToVirtualIndex(index);
      }
    },
    scrollToProductGroupNumber: (productGroupNumber) => {
      const index = rowIndexById.get(buildEntryRowId(productGroupNumber));

      if (index !== undefined) {
        scrollToVirtualIndex(index);
      }
    },
  }));

  const handleEntryRender = useCallback(
    (rowId: string) => {
      const element = virtualListContainerRef.current?.querySelector(`[data-row-id="${rowId}"]`);
      if (element) {
        rowVirtualizer.measureElement(element);
      }
    },
    [rowVirtualizer],
  );

  return (
    <div
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        width: "100%",
        position: "relative",
        marginBlock: "-12px", // 24px / 2
      }}
      data-test-id={testId}
      ref={virtualListContainerRef}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index];

        if (!row) {
          return null;
        }

        if (row.kind === "alternate") {
          return (
            <div
              key={row.rowId}
              data-index={virtualRow.index}
              data-row-id={row.rowId}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <AlternateProducts
                alternativesFor={row.designation}
                onClose={() => onCloseAlternateRow(row.rowId)}
                orderEntryNumber={getAlternateInsertPosition(row.anchorProductGroupNumber)}
                alternateProductsId={row.productCode}
                tableId={row.tableId}
                rowId={row.rowId}
                onAlternateAddedToCart={() => {
                  dispatch(cartPageLoadThunk());
                }}
                onRender={() => {
                  const element = virtualListContainerRef.current?.querySelector(`[data-row-id="${row.rowId}"]`);
                  if (element) {
                    rowVirtualizer.measureElement(element);
                  }
                }}
              />
            </div>
          );
        }

        if (row.kind === "chain") {
          return (
            <div
              key={row.rowId}
              data-index={virtualRow.index}
              data-row-id={row.rowId}
              data-test-id="chain-row"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <Box sx={{ my: "-13px", display: "flex", justifyContent: "center" }}>
                <ConnectedEntriesChain />
              </Box>
            </div>
          );
        }

        return (
          <div
            key={row.rowId}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualRow.start}px)`,
              paddingBlock: "12px", // 24px / 2
            }}
            data-index={virtualRow.index}
            data-row-id={row.rowId}
          >
            <CartEntry
              entry={row}
              editMode={editMode ?? false}
              onRender={handleEntryRender}
              onDeleteProduct={onDeleteProduct}
              onDeleteLine={onDeleteLine}
              onCopyLine={onCopyLine}
            />
          </div>
        );
      })}
    </div>
  );
};

const ProductEntryList = RegularProductEntryList;

export default memo(ProductEntryList);
